import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Plus,
  Loader2,
  ChevronRight,
  ChevronDown,
  Pencil,
  Archive,
  Lock,
  Building2,
} from "lucide-react";
import api from "../lib/api";
import Layout from "../components/Layout";
import EntityDialog from "../components/EntityDialog";
import { useAuth } from "../context/AuthContext";

const STATUS_STYLES = {
  active: "bg-[#4D5B46]/30 text-[#A3C293] border-[#4D5B46]/50",
  placeholder: "bg-elevated text-textmuted border-seam",
  planning: "bg-champagne/10 text-champagne border-champagne/30",
  setup: "bg-champagne/10 text-champagne border-champagne/30",
  pre_launch: "bg-champagne/15 text-champagne border-champagne/40",
  paused: "bg-[#5C2B29]/20 text-[#E5A09E] border-[#5C2B29]/40",
  dormant: "bg-elevated text-textmuted border-seam",
  restructuring: "bg-[#7a4a30]/20 text-[#E5B493] border-[#7a4a30]/50",
  closed: "bg-[#5C2B29]/30 text-[#E5A09E] border-[#5C2B29]/50",
};

const StatusPill = ({ status }) => (
  <span
    className={`text-[10px] tracking-wider uppercase px-2 py-0.5 rounded-full border ${
      STATUS_STYLES[status] || STATUS_STYLES.placeholder
    }`}
  >
    {(status || "placeholder").replace(/_/g, " ")}
  </span>
);

const TreeNode = ({ node, children, depth, expanded, onToggle, onEdit, onAddChild, onArchive, canManage }) => {
  const hasChildren = children && children.length > 0;
  return (
    <div className="border-b border-seam last:border-b-0">
      <div
        className="flex items-center gap-2 py-3 pr-3 hover:bg-elevated/40 transition-colors group"
        style={{ paddingLeft: `${12 + depth * 20}px` }}
        data-testid={`entity-row-${node.id}`}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={() => onToggle(node.id)}
            className="p-0.5 text-textmuted hover:text-champagne"
            data-testid={`entity-toggle-${node.id}`}
          >
            {expanded ? (
              <ChevronDown className="w-4 h-4" strokeWidth={1.6} />
            ) : (
              <ChevronRight className="w-4 h-4" strokeWidth={1.6} />
            )}
          </button>
        ) : (
          <span className="w-5" />
        )}

        <Building2 className="w-4 h-4 text-champagne shrink-0" strokeWidth={1.4} />

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-heading text-lg text-textmain truncate">{node.name}</span>
            {node.abbreviation && (
              <span className="text-[10px] tracking-wider uppercase text-champagne">{node.abbreviation}</span>
            )}
            {node.is_restricted && <Lock className="w-3 h-3 text-champagne" strokeWidth={1.6} />}
          </div>
          <div className="flex items-center gap-2 text-xs text-textmuted mt-0.5 flex-wrap">
            <span>{node.entity_type}</span>
            <StatusPill status={node.status} />
            {node.location && <span className="opacity-60">· {node.location}</span>}
          </div>
        </div>

        {canManage && (
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              type="button"
              onClick={() => onAddChild(node)}
              className="p-1.5 rounded-md text-textmuted hover:text-champagne hover:bg-elevated"
              title="Add child"
              data-testid={`entity-add-child-${node.id}`}
            >
              <Plus className="w-3.5 h-3.5" strokeWidth={1.6} />
            </button>
            <button
              type="button"
              onClick={() => onEdit(node)}
              className="p-1.5 rounded-md text-textmuted hover:text-champagne hover:bg-elevated"
              title="Edit"
              data-testid={`entity-edit-${node.id}`}
            >
              <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={() => onArchive(node)}
              className="p-1.5 rounded-md text-textmuted hover:text-[#E5A09E] hover:bg-elevated"
              title="Archive (marks closed)"
              data-testid={`entity-archive-${node.id}`}
            >
              <Archive className="w-3.5 h-3.5" strokeWidth={1.5} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const EnterpriseRegistry = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("enterprise.manage");

  const [entities, setEntities] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [parentPreset, setParentPreset] = useState(null);

  const load = useCallback(async () => {
    try {
      const [e, m] = await Promise.all([
        api.get("/enterprise/entities"),
        api.get("/enterprise/meta"),
      ]);
      setEntities(e.data);
      setMeta(m.data);
      // Default: expand root and top level
      const nextExpanded = {};
      e.data.forEach((x) => {
        if (!x.parent_id) nextExpanded[x.id] = true;
      });
      setExpanded((prev) => ({ ...nextExpanded, ...prev }));
    } catch (err) {
      toast.error("Could not load registry");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Build tree
  const childrenMap = useMemo(() => {
    const map = {};
    entities.forEach((e) => {
      const key = e.parent_id || "__root__";
      if (!map[key]) map[key] = [];
      map[key].push(e);
    });
    Object.keys(map).forEach((k) => map[k].sort((a, b) => a.name.localeCompare(b.name)));
    return map;
  }, [entities]);

  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const openAdd = (parent = null) => {
    setEditing(null);
    setParentPreset(parent?.id || null);
    setDialogOpen(true);
  };

  const openEdit = (node) => {
    setEditing(node);
    setParentPreset(null);
    setDialogOpen(true);
  };

  const archive = async (node) => {
    if (!window.confirm(`Archive "${node.name}"? History will be preserved; status becomes "closed".`)) return;
    try {
      await api.delete(`/enterprise/entities/${node.id}`);
      toast.success(`${node.name} archived`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Archive failed");
    }
  };

  const renderNode = (node, depth) => {
    const kids = childrenMap[node.id] || [];
    const isOpen = expanded[node.id];
    return (
      <React.Fragment key={node.id}>
        <TreeNode
          node={node}
          children={kids}
          depth={depth}
          expanded={isOpen}
          onToggle={toggle}
          onEdit={openEdit}
          onAddChild={openAdd}
          onArchive={archive}
          canManage={canManage}
        />
        {isOpen && kids.map((c) => renderNode(c, depth + 1))}
      </React.Fragment>
    );
  };

  const roots = childrenMap["__root__"] || [];

  return (
    <Layout>
      <section className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div className="max-w-2xl">
            <p className="label-eyebrow">R.S.V.P.</p>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mt-2">
              Enterprise Registry
            </h1>
            <p className="text-textmuted mt-3 leading-relaxed">
              Companies, subsidiaries, divisions, brands, applications, programs, projects — the
              authoritative structure of every operation R.S.V.P. stewards.
            </p>
          </div>
          {canManage && (
            <button
              type="button"
              onClick={() => openAdd(null)}
              data-testid="add-entity-btn"
              className="inline-flex items-center gap-2 self-start bg-champagne text-obsidian hover:bg-champagne-soft font-medium rounded-md px-5 py-2.5 transition-all shadow-[0_0_20px_rgba(212,175,55,0.15)]"
            >
              <Plus className="w-4 h-4" strokeWidth={1.7} />
              Add entity
            </button>
          )}
        </div>
      </section>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-6 h-6 text-champagne animate-spin" />
        </div>
      ) : roots.length === 0 ? (
        <div className="regal-card p-16 text-center">
          <h3 className="font-heading text-2xl mb-2">Registry not visible</h3>
          <p className="text-textmuted">You do not have permission to see enterprise entities.</p>
        </div>
      ) : (
        <div className="regal-card overflow-hidden">
          <div className="px-4 py-3 border-b border-seam bg-elevated/40 flex items-center justify-between">
            <span className="label-eyebrow">Hierarchy</span>
            <span className="text-xs text-textmuted">{entities.length} entities</span>
          </div>
          <div className="divide-y divide-seam">
            {roots.map((r) => renderNode(r, 0))}
          </div>
        </div>
      )}

      <EntityDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSaved={() => load()}
        entities={entities}
        meta={meta}
        initial={editing}
        parentPreset={parentPreset}
      />
    </Layout>
  );
};

export default EnterpriseRegistry;
